'use client'
/**
 * Chat socket identity — who this login connects to the chat server as.
 * ─────────────────────────────────────────────────────────────────────────────
 * Extracted from views/chat so the chat page and the app-wide connection cannot
 * drift apart. If they resolved identity differently, the two would connect with
 * different params, and connectChatSocket would tear one down to open the other
 * on every navigation — churning presence and dropping calls mid-ring.
 *
 * A Branch/Family admin's real scope cannot be read from settings alone, so it is
 * resolved against the API; everyone else is decided locally and is ready at once.
 */
import { useEffect, useMemo, useState } from 'react'

import { APIURL } from '@views/common/commonUrl'

export const SUPER_ADMIN_CODE = 'MEE'

export function cleanCode(v) { return v == null ? '' : String(v).trim() }
export function normCode(v) { return cleanCode(v).toUpperCase() }

export function getLoginCode(settings = {}) {
  return normCode(
    settings?.ACode ||
    settings?.clientId ||
    settings?.userId ||
    settings?.id ||
    settings?.username ||
    settings?.UserName ||
    settings?.selectedUser?.ACode ||
    settings?.selectedUser?.ClientCode ||
    settings?.selectedUser?.UserName ||
    ''
  )
}

export function getBranchFamilyCode(settings = {}) {
  const rows = Array.isArray(settings.branchFamily) ? settings.branchFamily : []
  const loginCode = getLoginCode(settings)

  // Admin login can keep admin code in settings.branch, while the real
  // Branch/Family code is stored in branchFamily[].name. Prefer that scoped
  // value so Branch Admin Hardik resolves to HO, not HARDIK.
  const branchRow = rows.find(r => {
    const type = cleanCode(r?.type || r?.RType || r?.rtype).toLowerCase()
    const name = cleanCode(r?.name || r?.branch || r?.Branch || r?.value || r?.code)

    return type.includes('branch') && name
  })

  if (branchRow) return normCode(branchRow.name || branchRow.branch || branchRow.Branch || branchRow.value || branchRow.code)

  const familyRow = rows.find(r => {
    const type = cleanCode(r?.type || r?.RType || r?.rtype).toLowerCase()
    const name = cleanCode(r?.name || r?.family || r?.Family || r?.value || r?.code)

    return type.includes('family') && name
  })

  if (familyRow) return normCode(familyRow.name || familyRow.family || familyRow.Family || familyRow.value || familyRow.code)

  const directBranch = cleanCode(settings.branch)
  const directFamily = cleanCode(settings.family)
  const direct = directBranch || directFamily

  if (direct && normCode(direct) !== loginCode) return normCode(direct)

  const anyRow = rows.find(r => cleanCode(r?.branch || r?.Branch || r?.family || r?.Family || r?.name || r?.value || r?.code))

  if (anyRow) return normCode(anyRow.branch || anyRow.Branch || anyRow.family || anyRow.Family || anyRow.name || anyRow.value || anyRow.code)

  return direct ? normCode(direct) : ''
}

export function getBranchFamilyType(settings = {}, branchCode = '') {
  const target = normCode(branchCode || getBranchFamilyCode(settings))
  const rows = Array.isArray(settings.branchFamily) ? settings.branchFamily : []

  const row = rows.find(r => {
    const code = normCode(r?.name || r?.branch || r?.Branch || r?.family || r?.Family || r?.value || r?.code)

    return target && code === target
  })

  const type = cleanCode(row?.type || row?.RType || row?.rtype)

  if (type.toLowerCase().includes('family')) return 'Family'
  if (type.toLowerCase().includes('branch')) return 'Branch'

  if (cleanCode(settings.family) && normCode(settings.family) === target && target !== getLoginCode(settings)) return 'Family'
  if (cleanCode(settings.branch) && normCode(settings.branch) === target && target !== getLoginCode(settings)) return 'Branch'

  return 'Branch'
}

export function getSocketType(settings = {}) {
  const loginCode = getLoginCode(settings)
  const loginType = cleanCode(settings?.type || settings?.Role || settings?.role).toLowerCase()

  if (settings?.superadmin || loginCode === SUPER_ADMIN_CODE) return 'super_admin'

  // Important: many normal clients also carry Branch/Family details in settings.
  // Do not treat every Branch/Family-bearing login as a Branch/Family Admin.
  // Only admin logins with Branch/Family context should connect as branch_admin.
  if (loginType === 'admin') {
    return getBranchFamilyCode(settings) ? 'branch' : 'company_admin'
  }

  return 'user'
}

export async function fetchResolvedChatProfile(settings = {}, fallbackBranch = '') {
  if (!settings?.accesstoken) return null

  const response = await global.axios.request({
    method: 'get',
    maxBodyLength: Infinity,
    url: APIURL + 'chat/v4/profile',
    // Bounded timeout: if the profile endpoint is slow/hangs (e.g. backend DB
    // busy during billing), reject instead of hanging so the caller's .catch
    // falls back to the local branch code and chat still connects.
    timeout: 8000,
    headers: {
      'Content-Type': 'application/json',
      'x-sess-key': settings.accesstoken,
      'x-sess-salt': '',
      'x-branch-code': fallbackBranch || '',
    },
  })

  const row = response?.data || {}

  return row?.ApiStatus === true ? (row.Profile || null) : null
}

/**
 * The connection parameters for this login, and whether they are settled yet.
 *
 * `ready` is false only while a Branch/Family admin's scope is being resolved.
 * Connecting before then would open the socket on the wrong branch and then have
 * to reconnect, so callers wait.
 */
export function useChatIdentity(settings) {
  const socketType = useMemo(() => getSocketType(settings || {}), [settings])
  const branchCode = useMemo(() => getBranchFamilyCode(settings || {}), [settings])

  const [scope, setScope] = useState({ code: '', type: 'Branch', ready: false })

  useEffect(() => {
    let cancelled = false

    if (!settings?.accesstoken) {
      setScope({ code: '', type: 'Branch', ready: false })

      return undefined
    }

    if (socketType !== 'branch') {
      setScope({ code: '', type: 'Branch', ready: true })

      return undefined
    }

    setScope(prev => ({ ...prev, ready: false }))

    fetchResolvedChatProfile(settings, branchCode)
      .then(apiProfile => {
        if (cancelled) return
        const resolvedCode = normCode(apiProfile?.branchId || apiProfile?.branch_id || branchCode)
        const resolvedType =
          cleanCode(apiProfile?.branchType || apiProfile?.branch_type || getBranchFamilyType(settings, resolvedCode)) || 'Branch'

        setScope({ code: resolvedCode, type: resolvedType, ready: true })
      })
      .catch(err => {
        if (cancelled) return
        console.warn('[Chat] Branch/Family resolve skipped:', err?.message || err)
        setScope({ code: normCode(branchCode), type: getBranchFamilyType(settings, branchCode), ready: true })
      })

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.accesstoken, socketType, branchCode])

  const effectiveBranchCode = normCode(scope.code || branchCode)

  return {
    socketType,
    branchCode,
    effectiveBranchCode,
    scopeType: scope.type,
    ready: scope.ready,
    // A branch login with no resolvable branch cannot connect at all — the
    // server rejects it — so this is reported rather than attempted.
    canConnect: Boolean(settings?.accesstoken) && scope.ready &&
      (socketType !== 'branch' || Boolean(effectiveBranchCode)),
  }
}
